import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import { LinkButton } from '@/components/LinkButton';
import { Modal } from '@/components/Modal';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsSmUp } from '@/hooks/useMedia';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/common';

import { AdjustTheGifts } from './components/AdjustTheGifts';
import { ExchangeTheGifts } from './components/ExchangeTheGifts';
import { GiftItDigitally } from './components/GiftItDigitally';
import { PlanYourGift } from './components/PlanYourGift';

import * as S from './SendGiftModal.styles';

export interface SendGiftModalProps {
  country: string;
  isOpen?: boolean;
  locale: string;
  onClose: () => void;
  sku?: string | null;
}

export const SendGiftModal = ({
  country,
  isOpen,
  locale,
  onClose,
  sku,
}: SendGiftModalProps) => {
  const { formatMessage } = useIntl();
  const isSmUp = useIsSmUp();
  const { sendEvent } = useGoogleAnalytics();

  const [activeTab, setActiveTab] = useState<'sender' | 'recipient'>('sender');

  const handleTabClick = (tab: 'sender' | 'recipient') => {
    setActiveTab(tab);
  };

  useEffect(() => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.forGift,
      });
    }
  }, [isOpen]);

  const handleModalClose = () => {
    sendEvent({
      event: EVENT_NAME.POPIN_CLOSE,
      popin_title: popinTitle.forGift,
    });

    onClose();
  };

  return (
    <S.Modal
      data-testid='SendGiftModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={handleModalClose}
    >
      <Modal.Header
        data-testid='SendGiftModalHeader'
        divider
        onClose={handleModalClose}
      >
        <SectionHeader
          sizeDesktop={30}
          sizeMobile={24}
          text={formatMessage(commonMessages.sendGiftHeader)}
        />
      </Modal.Header>
      <S.ModalBody data-testid='SendGiftModalBody'>
        <S.ModalBodyWrapper>
          <S.HeaderWrapper>
            <Typography textAlign='center'>
              {formatMessage(commonMessages.sendGiftDescription)}
            </Typography>
          </S.HeaderWrapper>

          <S.BodyInner>
            {isSmUp ? (
              <>
                <S.InfoBox>
                  <S.InfoBoxHeader>
                    {formatMessage(commonMessages.sendGiftSender)}
                  </S.InfoBoxHeader>
                  <GiftItDigitally />
                  <PlanYourGift />
                </S.InfoBox>
                <S.InfoBox>
                  <S.InfoBoxHeader>
                    {formatMessage(commonMessages.sendGiftRecipient)}
                  </S.InfoBoxHeader>
                  <AdjustTheGifts />
                  <ExchangeTheGifts />
                </S.InfoBox>
              </>
            ) : (
              <S.InfoBox>
                <S.InfoBoxHeaderMobContainer>
                  <S.InfoBoxHeaderTab
                    $isActive={activeTab === 'sender'}
                    onClick={() => handleTabClick('sender')}
                  >
                    {formatMessage(commonMessages.sendGiftSender)}
                  </S.InfoBoxHeaderTab>
                  <S.InfoBoxHeaderTab
                    $isActive={activeTab === 'recipient'}
                    onClick={() => handleTabClick('recipient')}
                  >
                    {formatMessage(commonMessages.sendGiftRecipient)}
                  </S.InfoBoxHeaderTab>
                </S.InfoBoxHeaderMobContainer>
                {activeTab === 'sender' && (
                  <S.InfoBoxBodyMobWrapper>
                    <GiftItDigitally />
                    <PlanYourGift />
                  </S.InfoBoxBodyMobWrapper>
                )}
                {activeTab === 'recipient' && (
                  <S.InfoBoxBodyMobWrapper>
                    <AdjustTheGifts />
                    <ExchangeTheGifts />
                  </S.InfoBoxBodyMobWrapper>
                )}
              </S.InfoBox>
            )}
          </S.BodyInner>
        </S.ModalBodyWrapper>
        <S.ButtonGroup>
          <LinkButton
            disabled={false}
            href={`${process.env.NEXT_PUBLIC_4_GIFTERS_URL}/${locale}/products/${sku}/gift?current_country=${country}`}
            // @ts-ignore
            target='_blank'
          >
            {formatMessage(commonMessages.sendGiftGetStarted)}
          </LinkButton>
          <S.LearnMore>
            <LegacyButton
              data-test-id='SendGiftModalButton'
              isFluid
              label={formatMessage(commonMessages.sendGiftLearnMore)}
              linkProps={{
                href: `${paths.service.faq.asPath()}#give-as-a-4gift`,
              }}
              openUrlInNewTab
              variant='link'
            >
              <Icon color='primary' name='chevronRight' />
            </LegacyButton>
          </S.LearnMore>
        </S.ButtonGroup>
      </S.ModalBody>
    </S.Modal>
  );
};
